import './mainMenu.css';

export interface MenuSaveSummary {
  hasSave: boolean;
  shipName: string | null;
  lastVerifiedName: string | null;
  namesCarved: number;
}

interface MenuHandlers {
  continueRecord(): void;
  newRecord(): void;
  openSettings(): void;
  openHangar(): void;
  openAudio(): void;
  openCredits(): void;
  quit(): void;
}

type MenuAction = 'continue' | 'new' | 'archive' | 'settings' | 'hangar' | 'audio' | 'credits' | 'quit';

const ACTIONS: readonly MenuAction[] = ['continue', 'new', 'archive', 'settings', 'hangar', 'audio', 'credits', 'quit'];

const esc = (value: string): string =>
  value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const formatSummary = (summary: MenuSaveSummary): string => {
  if (!summary.hasSave || !summary.shipName) return 'NO RECORD RECLAIMED.';
  const carved = summary.lastVerifiedName ?? 'NO VERIFIED NAME';
  return `${summary.shipName.toUpperCase()} · ${carved.toUpperCase()}`;
};

const detailText = (action: MenuAction, summary: MenuSaveSummary): string => {
  if (action === 'continue') return summary.hasSave ? formatSummary(summary) : 'NO RECORD RECLAIMED.';
  if (action === 'new') return 'BEGIN A FRESH ENTRY. THE COLD OPEN FOLLOWS AS IT DOES TODAY.';
  if (action === 'archive') return 'ARCHIVE INDEX SEALED FOR A LATER WAVE.';
  if (action === 'settings') return 'DISPLAY, GRAPHICS, AUDIO, CONTROLS, GAMEPLAY, ACCESSIBILITY.';
  if (action === 'hangar') return 'OPEN THE HANGAR LEDGER AND OUTFIT THE HULL. A NEW LEAF OPENS BESIDE THIS ONE.';
  if (action === 'audio') return 'OPEN THE AUDIO REGISTER. A NEW LEAF OPENS BESIDE THIS ONE.';
  if (action === 'credits') return 'OPEN THE MEMORIAL ROLL AND THE ARCHIVE OF SOURCES KEPT BEHIND THE SHELL.';
  return 'RETURN TO THE CENOTAPH REGISTER.';
};

const labelFor = (action: MenuAction): string => {
  if (action === 'continue') return 'CONTINUE';
  if (action === 'new') return 'NEW RECORD';
  if (action === 'archive') return 'ARCHIVE';
  if (action === 'settings') return 'SETTINGS';
  if (action === 'hangar') return 'HANGAR';
  if (action === 'audio') return 'AUDIO';
  if (action === 'credits') return 'CREDITS';
  return 'QUIT';
};

export class MainMenu {
  private readonly root: HTMLDivElement;
  private readonly detail: HTMLDivElement;
  private readonly footer: HTMLDivElement;
  private buttons: HTMLButtonElement[] = [];
  private summary: MenuSaveSummary | null = null;
  private handlers: MenuHandlers | null = null;
  private open = false;
  private selected: MenuAction = 'new';

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'main-menu';
    this.root.innerHTML = `
      <div class="mm-card">
        <div class="mm-head">
          <div class="mm-folio">FOLIO 001</div>
          <div class="mm-title">A RECORD OF THE LOST</div>
        </div>
        <div class="mm-body">
          <div class="mm-items"></div>
          <div class="mm-detail"></div>
        </div>
        <div class="mm-foot"></div>
      </div>`;
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
    this.detail = this.root.querySelector('.mm-detail') as HTMLDivElement;
    this.footer = this.root.querySelector('.mm-foot') as HTMLDivElement;
    this.root.addEventListener('click', (event) => this.onClick(event));
    this.root.addEventListener('focusin', (event) => this.onFocus(event));
    window.addEventListener('keydown', (event) => this.onKey(event));
  }

  show(summary: MenuSaveSummary, handlers: MenuHandlers): void {
    this.summary = summary;
    this.handlers = handlers;
    this.selected = summary.hasSave ? 'continue' : 'new';
    this.render();
    this.open = true;
    this.root.style.display = 'flex';
    this.root.classList.add('shown');
    this.focusSelected();
  }

  hide(): void {
    this.open = false;
    this.root.classList.remove('shown');
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.hide();
    this.root.remove();
  }

  private render(): void {
    const summary = this.summary;
    if (!summary) return;
    const items = ACTIONS.map((action) => this.buttonMarkup(action, summary)).join('');
    const host = this.root.querySelector('.mm-items') as HTMLDivElement;
    host.innerHTML = items;
    this.buttons = [...host.querySelectorAll('button')];
    this.footer.textContent = `FOLIO 001 · ${summary.namesCarved} ${summary.namesCarved === 1 ? 'NAME' : 'NAMES'} CARVED`;
    this.updateDetail(this.selected);
  }

  private buttonMarkup(action: MenuAction, summary: MenuSaveSummary): string {
    // UI dignity: ARCHIVE is sealed content, not a live destination — etched,
    // aria-disabled, unfocusable, its detail line still explains itself on hover.
    const sealed = action === 'archive';
    const disabled = (action === 'continue' && !summary.hasSave) || sealed;
    const state = disabled ? ' disabled aria-disabled="true"' : '';
    const current = action === this.selected ? ' current' : '';
    const cls = `mm-item${current}${sealed ? ' mm-sealed' : ''}`;
    const note = action === 'continue' ? `<span class="mm-note">${esc(formatSummary(summary))}</span>` : '';
    return `<button class="${cls}" data-action="${action}"${state}>${labelFor(action)}${note}</button>`;
  }

  private onClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
    if (!button || button.disabled) return;
    this.activate(button.dataset.action as MenuAction);
  }

  private onFocus(event: FocusEvent): void {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
    if (!button || button.disabled) return;
    this.updateDetail(button.dataset.action as MenuAction);
  }

  private onKey(event: KeyboardEvent): void {
    if (!this.open) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveFocus(event.key === 'ArrowDown' ? 1 : -1);
    }
  }

  private moveFocus(step: 1 | -1): void {
    const enabled = this.buttons.filter((button) => !button.disabled);
    const current = enabled.indexOf(document.activeElement as HTMLButtonElement);
    const next = current === -1 ? 0 : (current + step + enabled.length) % enabled.length;
    enabled[next]?.focus();
  }

  private focusSelected(): void {
    const button = this.buttons.find((item) => item.dataset.action === this.selected && !item.disabled);
    button?.focus();
  }

  private updateDetail(action: MenuAction): void {
    if (!this.summary) return;
    this.selected = action;
    this.detail.textContent = detailText(action, this.summary);
    for (const button of this.buttons) {
      button.classList.toggle('current', button.dataset.action === action);
    }
  }

  private activate(action: MenuAction): void {
    if (!this.handlers || !this.summary) return;
    this.updateDetail(action);
    if (action === 'continue' && this.summary.hasSave) this.handlers.continueRecord();
    else if (action === 'new') this.handlers.newRecord();
    else if (action === 'settings') this.handlers.openSettings();
    else if (action === 'hangar') this.handlers.openHangar();
    else if (action === 'audio') this.handlers.openAudio();
    else if (action === 'credits') this.handlers.openCredits();
    else if (action === 'quit') this.handlers.quit();
  }
}
